import { useAccount, useNetwork, useSwitchNetwork } from 'wagmi';
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from 'sonner';
import { logger } from '@/utils/logger';
import { useWalletConnectGas } from '@/hooks/useWalletConnectGas';
import { useNetworkRestrictions } from '@/hooks/useNetworkRestrictions';
import { 
  NetworkAvaxIcon, 
  NetworkBNBIcon, 
  NetworkBSCTestnetIcon, 
  NetworkETHIcon,
  NetworkAbstractIcon,
  NetworkPolygonIcon
} from '@/components/icons/NetworkIcons';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import NetworkLoadingScreen from '@/components/NetworkLoadingScreen';

// Network options with custom icons - Alphabetically ordered
const allNetworks = [
  { 
    name: 'Abstract', 
    chainId: 2741, 
    icon: (props: any) => <NetworkAbstractIcon {...props} /> 
  },
  { 
    name: 'Abstract Sepolia', 
    chainId: 11124, 
    icon: (props: any) => <NetworkAbstractIcon {...props} /> // Using Abstract icon for Abstract Sepolia
  },
  { 
    name: 'Arbitrum One', 
    chainId: 42161, 
    icon: (props: any) => <img src="/Crypto Logos/Arbitrum logo custom.png" alt="Arbitrum One" {...props} /> 
  },
  { 
    name: 'Arbitrum Sepolia', 
    chainId: 421614, 
    icon: (props: any) => <img src="/Crypto Logos/Arbitrum logo custom.png" alt="Arbitrum Sepolia" {...props} /> 
  },
  { 
    name: 'Avalanche', 
    chainId: 43114, 
    icon: (props: any) => <NetworkAvaxIcon {...props} /> 
  },
  { 
    name: 'Avalanche Fuji', 
    chainId: 43113, 
    icon: (props: any) => <NetworkAvaxIcon {...props} /> // Using AVAX icon for Fuji
  },
  { 
    name: 'Base', 
    chainId: 8453, 
    icon: (props: any) => <img src="/Crypto Logos/BASE.png" alt="Base" {...props} /> 
  },
  { 
    name: 'Base Sepolia', 
    chainId: 84532, 
    icon: (props: any) => <img src="/Crypto Logos/BASE.png" alt="Base Sepolia" {...props} /> 
  },
  { 
    name: 'BSC', 
    chainId: 56, 
    icon: (props: any) => <NetworkBNBIcon {...props} /> 
  },
  { 
    name: 'BSC Testnet', 
    chainId: 97, 
    icon: (props: any) => <NetworkBSCTestnetIcon {...props} /> 
  },
  { 
    name: 'Ethereum', 
    chainId: 1, 
    icon: (props: any) => <NetworkETHIcon {...props} /> 
  },
  { 
    name: 'opBNB', 
    chainId: 204, 
    icon: (props: any) => <img src="/Crypto Logos/opBNB logo MDBAPP.IO.png" alt="opBNB" {...props} /> 
  },
  { 
    name: 'opBNB Testnet', 
    chainId: 5611, 
    icon: (props: any) => <img src="/Crypto Logos/opBNB logo MDBAPP.IO.png" alt="opBNB Testnet" {...props} /> 
  },
  { 
    name: 'OP Mainnet', 
    chainId: 10, 
    icon: (props: any) => <img src="/Crypto Logos/OP.png" alt="OP Mainnet" {...props} /> 
  },
  { 
    name: 'OP Sepolia', 
    chainId: 11155420, 
    icon: (props: any) => <img src="/Crypto Logos/OP.png" alt="OP Sepolia" {...props} /> 
  },
  { 
    name: 'Polygon', 
    chainId: 137, 
    icon: (props: any) => <NetworkPolygonIcon {...props} /> 
  },
  { 
    name: 'Polygon Amoy', 
    chainId: 80002, 
    icon: (props: any) => <NetworkPolygonIcon {...props} /> 
  },
  { 
    name: 'Sei', 
    chainId: 1329, 
    icon: (props: any) => <img src="/Crypto Logos/Sei Custom Logo MdbApp.io.png" alt="Sei" {...props} /> 
  },
  { 
    name: 'Sei Atlantic', 
    chainId: 1328, 
    icon: (props: any) => <img src="/Crypto Logos/Sei Custom Logo MdbApp.io.png" alt="Sei Arctic" {...props} /> 
  },
  { 
    name: 'Sepolia', 
    chainId: 11155111, 
    icon: (props: any) => <NetworkETHIcon {...props} /> // Using ETH icon for Sepolia
  },
];

// Analytics tracking for network switches
const trackNetworkSwitch = (fromNetwork: string, toNetwork: string) => {
  // Add analytics tracking here if needed
  logger.info(`Network switch: ${fromNetwork} -> ${toNetwork}`);
};

export default function NetworkSwitch() {
  const { chain } = useNetwork();
  const { isConnected } = useAccount();
  const { switchNetwork, isLoading } = useSwitchNetwork();
  const { getGasPrice } = useWalletConnectGas();
  const { getAllowedNetworks } = useNetworkRestrictions();
  const navigate = useNavigate();
  
  // State for network loading screen
  const [showNetworkLoading, setShowNetworkLoading] = useState(false);
  const [loadingNetworkIcon, setLoadingNetworkIcon] = useState<string>('');
  const [loadingNetworkName, setLoadingNetworkName] = useState<string>('');

  // Filter networks based on admin status
  const allowedNetworkIds = getAllowedNetworks().map(network => network.chainId);
  let networks = allNetworks.filter(network => allowedNetworkIds.includes(network.chainId));

  // Always-available EVM networks (must always be visible): BSC (Mainnet), Avalanche, Abstract, Arbitrum One, Optimism, Base, Polygon, Sei, and Ethereum
  const ALWAYS_EVM_CHAIN_IDS = [56, 43114, 2741, 42161, 10, 8453, 137, 1329, 1, 204];
  const presentIds = new Set(networks.map(n => n.chainId));
  for (const id of ALWAYS_EVM_CHAIN_IDS) {
    if (!presentIds.has(id)) {
      const n = allNetworks.find(an => an.chainId === id);
      if (n) networks.push(n);
    }
  }
  
  // Sort networks alphabetically by name to maintain consistent order
  networks.sort((a, b) => a.name.localeCompare(b.name));

  // Helper function to get network icon path
  const getNetworkIconPath = (chainId: number): string => {
    const iconMap: Record<number, string> = {
      2741: '/Crypto Logos/Abstract-logo.png', // Abstract Mainnet
      11124: '/Crypto Logos/Abstract-logo.png', // Abstract Sepolia
      42161: '/Crypto Logos/Arbitrum logo custom.png',
      421614: '/Crypto Logos/Arbitrum logo custom.png',
      43114: '/Crypto Logos/AVAX.png',
      43113: '/Crypto Logos/AVAX.png',
      8453: '/Crypto Logos/BASE.png',
      84532: '/Crypto Logos/BASE.png',
      56: '/Crypto Logos/BNB_chain.png', // BSC Mainnet
      97: '/Crypto Logos/BSC Testnet.png', // BSC Testnet
      1: '/Crypto Logos/ETH.png',
      11155111: '/Crypto Logos/ETH.png',
      204: '/Crypto Logos/opBNB logo MDBAPP.IO.png',
      5611: '/Crypto Logos/opBNB logo MDBAPP.IO.png',
      10: '/Crypto Logos/OP.png',
      11155420: '/Crypto Logos/OP.png',
      137: '/Crypto Logos/MATIC.png',
      80002: '/Crypto Logos/MATIC.png',
      1329: '/Crypto Logos/Sei Custom Logo MdbApp.io.png',
      1328: '/Crypto Logos/Sei Custom Logo MdbApp.io.png',
    };
    return iconMap[chainId] || '/Dynamic Header Logos/MdbApp.io Default Logo.png';
  };

  // Handle network switching using WalletConnect's infrastructure
  const handleNetworkSwitch = async (chainId: number) => {
    // If there's no wallet connected or switching isn't available, quietly ignore
    if (!isConnected || !switchNetwork) {
      logger.info('Network switch attempted without a connected wallet or unsupported adapter. Ignoring.');
      return;
    }
    const fromNetwork = chain?.name || 'unknown';
    const targetNetwork = allNetworks.find(n => n.chainId === chainId);
    const toNetwork = targetNetwork?.name || 'unknown';
    
    try {
      logger.info(`Attempting to switch from ${fromNetwork} to ${toNetwork}`);
      
      // Show network loading screen with network icon
      setLoadingNetworkIcon(getNetworkIconPath(chainId));
      setLoadingNetworkName(toNetwork);
      setShowNetworkLoading(true);
      
      // Store network change info for potential refresh handling
      localStorage.setItem('lastNetworkChange', JSON.stringify({
        timestamp: Date.now(),
        fromChainId: chain?.id,
        toChainId: chainId
      }));
      
      // Use WalletConnect's infrastructure via wagmi
      if (switchNetwork) {
        await switchNetwork(chainId);
        
        // Get gas price from WalletConnect's infrastructure after network switch
        try {
          const gasPrice = await getGasPrice();
          logger.info(`Gas price on ${toNetwork}: ${gasPrice}`);
        } catch (error) {
          logger.warn(`Connected to ${toNetwork} but couldn't fetch gas price`);
        }
        
        // Track successful network switch
        trackNetworkSwitch(fromNetwork, toNetwork);
      }
    } catch (error: any) {
      logger.error(`Failed to switch network: ${error.message}`);
      toast.error(`Failed to switch network: ${error.message}`);
    }
  };

  // Get current network icon or use BSC as default when no wallet is connected
  const currentNetwork = allNetworks.find(n => n.chainId === chain?.id);
  const NetworkIcon = currentNetwork?.icon || ((props: any) => <NetworkBNBIcon {...props} />);

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost"
          size="icon"
          className="inline-flex items-center justify-center h-9 w-9 hover:bg-accent rounded-md border-2 group touch-manipulation"
          style={{ borderColor: 'var(--color-header-accent, #0A74FF)' }}
          disabled={isLoading}
        >
          <div className="relative w-5 h-5">
            {isLoading ? (
              <span className="animate-pulse">⏳</span>
            ) : (
              <NetworkIcon className="h-5 w-5" />
            )}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {networks.map((network) => {
          const isSameChain = network.chainId === chain?.id;
          const isHardDisabled = isLoading || isSameChain; // still use native disabled for these
          const label = network.chainId === 56 ? 'BSC/BNB' : network.name;

          const item = (
            <DropdownMenuItem
              key={network.chainId}
              onClick={() => {
                if (!isConnected) return; // ignore click if not connected
                handleNetworkSwitch(network.chainId)
              }}
              onSelect={(e) => {
                if (!isConnected) {
                  // prevent menu from closing when user attempts to click while disconnected
                  e.preventDefault();
                }
              }}
              disabled={isHardDisabled}
              aria-disabled={!isConnected}
              className={`flex items-center gap-2 ${!isConnected ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {network.icon({ className: "h-5 w-5" })}
              <span>{label}</span>
            </DropdownMenuItem>
          );

          if (!isConnected && !isHardDisabled) {
            // Wrap with tooltip only when not connected and item isn't natively disabled
            return (
              <TooltipProvider key={network.chainId}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {item}
                  </TooltipTrigger>
                  <TooltipContent>
                    Connect wallet to change networks
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          return item;
        })}
        {/* Always-visible external link items (do not switch network) */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            window.location.href = '/solanatokenlauncherapp';
          }}
          className="flex items-center gap-2 cursor-pointer"
        >
          <img src="/Crypto%20Logos/SOL.png" alt="Solana" className="h-5 w-5" />
          <span>Solana</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            window.location.href = '/suitokenlauncher';
          }}
          className="flex items-center gap-2 cursor-pointer"
        >
          <img src="/Crypto%20Logos/SUI.png" alt="SUI" className="h-5 w-5" />
          <span>SUI</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    
    {/* Network Loading Screen Easter Egg */}
    {showNetworkLoading && (
      <NetworkLoadingScreen
        networkIcon={loadingNetworkIcon}
        networkName={loadingNetworkName}
        onComplete={() => setShowNetworkLoading(false)}
      />
    )}
    </>
  );
}
